import React, { useState, useContext, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Row, Col } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import moduleConfig from "./moduleConfig";
import { Store as toast } from "react-notifications-component";
import { BreadcrumbContext } from "../../../contexts/BreadcrumbContext";
import axios from "../../../config/axios";
import { toastNotification } from "../../../config/toastNotification";
import DashboardLayout from "../../UI/layouts/DashboardLayout";
import { ContainerLoading } from "../../UI/loading/ContainerLoading";
import BlockErrorMessage from "../../UI/errorMessages/BlockErrorMessage";
import ModuleHeader from "../../UI/modules/views/partials/common/ModuleHeader";
import { isEmpty } from "../../../helpers/CommonHelper";
import FormErrorMessage from "../../UI/errorMessages/FormErrorMessage";
import CMSRichTextEditor from "../../UI/forms/CMSRichTextEditor";
import CMSFormInput from "../../UI/forms/CMSFormInput";
import CMSFormDate from "../../UI/forms/CMSFormDate";
import CMSFormSelect from "../../UI/forms/CMSFormSelect";
import CMSFormFile from "../../UI/forms/CMSFormFile";
import CMSFormImageWithCrop from "../../UI/forms/CMSFormImageWithCrop";
import CMSFormRichText from "../../UI/forms/CMSFormRichText";
import CMSFormImage from "../../UI/forms/CMSFormImage";
import {
  updateInitialState,
  updateReducer,
} from "../../../reducers/module/updateReducer";
import {
  loadItemGeneral,
  onSubmitGeneral,
} from "../../../helpers/ModuleHelperFunctions";
import CMSFormColor from "../../UI/forms/CMSFormColor";
import CMSFormMultiSelect from "../../UI/forms/CMSFormMultiSelect";

const ProductUpdate = () => {
  const [updateState, updateDispatch] = useReducer(
    updateReducer,
    updateInitialState
  );
  const [formInitialValues, setFormInitialValues] = useState({});
  const { setBreadcrumb } = useContext(BreadcrumbContext);

  let { id } = useParams();
  let navigate = useNavigate();

  const loadItem = async () => {
    await loadItemGeneral(id, `${moduleConfig.url}/${id}`, updateDispatch);
  };
  useEffect(() => {
    loadItem();
    setBreadcrumb([
      moduleConfig.breadcrumbs.listing,
      // moduleConfig.breadcrumbs.details,
      moduleConfig.breadcrumbs.update,
    ]);
  }, [id]);

  useEffect(() => {
    if (updateState.data.item && updateState.data.item._id) {
      setFormInitialValues(updateState.data.item);
    }
  }, [updateState.data]);
  let imageRequirement = {
    dimension: {
      width: 500,
      height: 500,
    },
    allowedExtensions: ["image/png", "image/jpg", "image/jpeg"],
    size: 1024 * 1024 * 3,
  };
  return (
    <DashboardLayout>
      {updateState.status === "fetching" && updateState.isLoading && (
        <ContainerLoading />
      )}
      {updateState.error && updateState.status === "fetchFailed" ? (
        <BlockErrorMessage error={updateState.error} />
      ) : (
        <>
          <ModuleHeader
            moduleTitle={`Update ${moduleConfig.singleItemTitle}`}
            moduleUrl={moduleConfig.url}
          ></ModuleHeader>

          {Object.keys(formInitialValues).length > 0 &&
            updateState.data &&
            updateState.data.item &&
            !isEmpty(updateState.data.item._id) && (
              <section className="content">
                <Card>
                  <Card.Body>
                    {updateState.isLoading && (
                      <div className="overlay position-absolute containerLoading">
                        <i className="fas fa-2x fa-sync-alt fa-spin"></i>
                      </div>
                    )}
                    <Formik
                      initialValues={{
                        ...formInitialValues,
                        file: updateState.data.item.image,
                        //slideImageFile:updateState.data.item.slideImage
                      }}
                      validationSchema={yup.object({
                        title: yup
                          .string()
                          .required("Title is required")
                          .max(
                            100,
                            "Title should contain at most 100 characters"
                          ),

                        categories: yup
                          .array()
                          .of(yup.string())
                          .min(1, "Please select at least one category"),

                        materials: yup.array().of(yup.string()).nullable().min(1, "Please select at least one material"),

                        description: yup
                          .string()
                          .required("Description is required"),

                        file: yup
                          .mixed()
                          .required("Please upload an image")
                          .imageTypeCheck(
                            moduleConfig.imageRequirement.allowedExtensions
                          )
                          .imageSizeCheck(moduleConfig.imageRequirement.size),

                        brand: yup.string().nullable(),

                        price: yup
                          .number()
                          .required("Price is required")
                          .transform((value, originalValue) =>
                            originalValue === "" ? null : value
                          )
                          .nullable()
                          .typeError("Price must be a valid number"),

                        salePrice: yup
                          .number()
                          .required("Sale price is required")
                          .typeError("Sale price must be a valid number"),

                        amazonLink: yup
                          .string()
                          .url("Please enter a valid Amazon URL")
                          .nullable(),

                        noonLink: yup
                          .string()
                          .url("Please enter a valid Noon URL")
                          .nullable(),

                        inStock: yup
                          .string()
                          .required("Stock status is required"),

                        status: yup.string().required("Status is required"),
                        showOnHomePage: yup.string().required("Show on home page is required"),
                      })}
                      enableReinitialize={true}
                      validateOnChange={true}
                      validateOnBlur={true}
                      onSubmit={async (values, { resetForm }) => {
                        const formData = new FormData();
                        for (let name in values) {
                          if (name === "categories" || name === "materials") {
                            if (Array.isArray(values[name])) {
                              for (var i = 0; i < values[name].length; i++) {
                                formData.append(name + "[]", values[name][i]);
                              }
                            }
                          } else if (Array.isArray(values[name])) {
                            formData.append(name, JSON.stringify(values[name]));
                          } else {
                            formData.append(name, values[name]);
                          }
                        }
                        await onSubmitGeneral(
                          formData,
                          `${moduleConfig.url}/${id}`,
                          moduleConfig.url,
                          updateDispatch,
                          navigate
                        );
                      }}
                    >
                      {(form) => (
                        <Form onSubmit={form.handleSubmit}>
                          {/* {console.log(form.errors)} */}
                          {updateState.error && (
                            <FormErrorMessage error={updateState.error} />
                          )}

                          <CMSFormInput
                            name="title"
                            label="Title"
                            required={true}
                            form={form}
                          />

                          <CMSFormMultiSelect
                            name="categories"
                            label="Categories"
                            selectionValue="value"
                            selected={form.values.categories}
                            required={true}
                            form={form}
                            options={
                              updateState.data && updateState.data.categories
                                ? updateState.data.categories
                                : []
                            }
                            displayValue="label"
                          />

                          <CMSFormMultiSelect
                            name="materials"
                            label="Materials"
                            selectionValue="value"
                            selected={form.values.materials}
                            required={true}
                            form={form}
                            options={
                              updateState.data && updateState.data.materials
                                ? updateState.data.materials
                                : []
                            }
                            displayValue="label"
                          />

                          <CMSFormInput
                            name="brand"
                            label="Brand"
                            required={false}
                            form={form}
                          />

                          <CMSFormInput
                            name="price"
                            label="Price"
                           info="Add the sale price when there is no discount; if a discount exists, apply the original price instead."
                            form={form}
                            required={true}
                          />

                          <CMSFormInput
                            name="salePrice"
                            label="Sale Price"
                            info=""
                            form={form}
                            required={true}
                          />

                          <CMSFormImageWithCrop
                            label="Image"
                            name="file"
                            info="Recommended dimensions 500*500"
                            form={form}
                              aspect={1 / 1}
                            outputSize={imageRequirement.dimension}
                            required={true}
                          />

                          <CMSFormRichText
                            name="description"
                            label="Description"
                            form={form}
                            required={true}
                          />

                          <CMSFormInput
                            name="amazonLink"
                            label="Amazon Link"
                            required={false}
                            form={form}
                          />

                          <CMSFormInput
                            name="noonLink"
                            label="Noon Link"
                            required={false}
                            form={form}
                          />

                          <CMSFormSelect
                            name="inStock"
                            label="In-Stock"
                            required={true}
                            form={form}
                            options={[
                              { label: "Yes", value: "1" },
                              { label: "No", value: "0" },
                            ]}
                          />

                          <CMSFormSelect
                            name="showOnHomePage"
                            label="Show On Home Page?"
                            required={true}
                            form={form}
                            options={[
                              { label: "Yes", value: "1" },
                              { label: "No", value: "0" },
                            ]}
                          />

                          <CMSFormSelect
                            name="status"
                            label="Status"
                            required={true}
                            form={form}
                            options={[
                              { label: "Select Status...", value: "" },
                              { label: "Enabled", value: "1" },
                              { label: "Disabled", value: "0" },
                            ]}
                          />
                          <button className="btn btn-primary" type="submit">
                            Submit
                          </button>
                        </Form>
                      )}
                    </Formik>
                  </Card.Body>
                </Card>
              </section>
            )}
        </>
      )}
    </DashboardLayout>
  );
};
export default ProductUpdate;
